import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { devFilesApi } from '../../services/api';
import { useNotifications } from '../../context/NotificationContext';
import { useAuth } from '../../context/AuthContext';
import { useLookups } from '../../context/LookupContext';
import FilterableDataGrid from '../../components/common/FilterableDataGrid';
import { suggestNextVersion } from '../../utils/version';
import { hasColumnFilters, resolveSelection, NO_MATCH_LIST, SEARCH_HINT } from '../../utils/listSearch';
import SearchClearIcon from '../../components/common/SearchClearIcon';
import { useListTrunk } from '../../hooks/useListTrunk';
import { useLocation } from 'react-router-dom';
import { entryMemoryKey, loadView, saveView } from '../../utils/viewMemory';
import NoMatchState from '../../components/common/NoMatchState';
import AdvancedSearchModal from '../../components/common/AdvancedSearchModal';
import {
  Check, Save, X, Plus, Trash2, Pencil, RefreshCw, ListOrdered, ArrowUpDown,
  ChevronLeft, ChevronRight, GripVertical, ClipboardList,
  List, Table, FilterX, ListChecks, ListFilter
} from 'lucide-react';
import StuiSelect from '../../components/common/StuiSelect';


// `comment` holds the full plan body for each step — ~267K characters across the plan against
// ~86K in the descriptions — so omitting it hid three quarters of the text from the search box.
// `ownerName` is here because the grid and list both RENDER the display name while the old
// predicate only matched the slug, so searching "Neville" found nothing.
// What search reads, labelled as the user sees it. The labels double as field: prefixes and feed
// the Advanced Search builder's field list.
const SEARCH_FIELD_LABELS = [
  { key: 'step', label: 'Step' }, { key: 'name', label: 'Name' }, { key: 'description', label: 'Description' },
  { key: 'comment', label: 'Comment' }, { key: 'type', label: 'Type' }, { key: 'status', label: 'Status' },
  { key: 'owner', label: 'Owner' }, { key: 'ownerName', label: 'Owner Name' },
];
const SEARCH_FIELDS = SEARCH_FIELD_LABELS.map(f => f.key);

// Person columns filter on the display name the API resolves (ownerName / originatorName), so
// the filter list reads the same "Last, First" the cells show instead of login handles. A view
// saved before that kept handle values under owner / originator, which would now match nothing
// while still counting as an active filter, so those keys are dropped on load.
const dropHandleFilters = (filters) => {
  if (!filters) return filters;
  const { owner, originator, ...rest } = filters; // eslint-disable-line no-unused-vars
  return rest;
};

// Vocabularies live in AppCfg_LookupValues, the same rows the API validates against.
const LK_STATUS = 'DevPlan_Status';
const LK_TYPE = 'DevPlan_Type';

// The close wizard owns 'Complete' -- it renumbers the plan and bumps the major version,
// so the API rejects it on a general update and it must not appear in a picker.
const GUARDED_STATUS = 'Complete';

const DevPlanPage = () => {
  // ── Shared ───────────────────────────────────────────────────────────────────
  const [devPlan, setDevPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  // How this page was left in THIS history entry (utils/viewMemory): Back restores it, a nav
  // click or new link is a new entry and starts clean. Read once per mount.
  const location = useLocation();
  const memoryKey = entryMemoryKey(location, 'dev-plan');
  const [saved] = useState(() => loadView(memoryKey));
  const [viewMode, setViewMode] = useState(saved?.view === 'datasheet' ? 'datasheet' : 'list');
  const [searchTerm, setSearchTerm] = useState(saved?.search || '');
  const { notify } = useNotifications();
  const { currentUser } = useAuth();
  const currentUserSlug = currentUser?.email?.split('@')[0]?.split('.')[0]?.toLowerCase() || null;

  // ── List view state ──────────────────────────────────────────────────────────
  const [pickedStep, setSelectedStep] = useState(saved?.picked != null ? { id: saved.picked } : null);
  const [dsSort, setDsSort] = useState(saved?.sort?.key ? saved.sort : null); // header sort, shared by both views
  const [showAdvSearch, setShowAdvSearch] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [listCollapsed, setListCollapsed] = useState(false);
  const { getValues } = useLookups();
  const statusOptions = getValues(LK_STATUS).filter(v => v !== GUARDED_STATUS);
  const typeOptions = getValues(LK_TYPE);
  // Release is stamped only by Pull From PreProd, never chosen.
  const revLogCategoryOptions = getValues('RevLog_Category').filter(c => c !== 'Release');
  const [roster, setRoster] = useState([]);
  const [showAddStep, setShowAddStep] = useState(false);
  const [newStep, setNewStep] = useState({ name: '', description: '', comment: '', status: 'Not Started', type: 'Core', owner: currentUserSlug });
  const [showCloseStep, setShowCloseStep] = useState(false);
  const [closeStepPage, setCloseStepPage] = useState(1);
  const [closeStepForm, setCloseStepForm] = useState({ description: '', comment: '', category: '' });
  const [completePreviewItems, setCompletePreviewItems] = useState([]);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  // ── Datasheet state ──────────────────────────────────────────────────────────
  const [dsFilters, setDsFilters] = useState(dropHandleFilters(saved?.filters) || { status: ['Not Started', 'Pending', 'In Progress'] });
  const [dsEditMode, setDsEditMode] = useState(false);
  const [dsEdits, setDsEdits] = useState({});
  const [dsMenuOpen, setDsMenuOpen] = useState(false);
  const dsMenuRef = useRef(null);
  const [dsSortPending, setDsSortPending] = useState(false);
  const [showDescModal, setShowDescModal] = useState(false);
  const [descModalStep, setDescModalStep] = useState(null);
  const [descModalEditMode, setDescModalEditMode] = useState(false);
  const [descLocal, setDescLocal] = useState({ description: '', comment: '' });

  // ── Data loading ─────────────────────────────────────────────────────────────
  const loadDevPlan = useCallback(async () => {
    try {
      setLoading(true);
      const response = await devFilesApi.getDevPlan();
      setDevPlan(response.data);
      setSelectedStep(prev => {
        if (prev) return prev;
        const incomplete = response.data.steps.find(s => s.status !== 'Complete');
        return incomplete || response.data.steps[0] || null;
      });
    } catch (err) {
      notify.error(err.response?.data?.message || 'Failed to load dev plan');
    } finally {
      setLoading(false);
    }
  }, [notify]);

  useEffect(() => { loadDevPlan(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
      if (dsMenuRef.current && !dsMenuRef.current.contains(e.target)) setDsMenuOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // ── List view handlers ───────────────────────────────────────────────────────
  const handleSelectStep = (step) => {
    if (isEditing) {
      notify.confirm('Discard unsaved changes?', () => {
        setIsEditing(false);
        setEditForm({});
        setSelectedStep(step);
      });
      return;
    }
    setSelectedStep(step);
  };

  const startEditing = () => {
    if (!selectedStep) return;
    setEditForm({ ...selectedStep });
    setIsEditing(true);
  };

  const cancelEditing = () => { setIsEditing(false); setEditForm({}); };

  const handleSave = async () => {
    try {
      setSaving(true);
      await devFilesApi.updateDevPlanStep(editForm.step, editForm);
      setIsEditing(false);
      setEditForm({});
      await loadDevPlan();
    } catch (err) {
      notify.error(err.response?.data?.message || 'Failed to save step');
    } finally {
      setSaving(false);
    }
  };

  const handleAddStep = async () => {
    if (!newStep.name.trim()) return;
    try {
      setSaving(true);
      await devFilesApi.addDevPlanStep(newStep);
      setNewStep({ name: '', description: '', comment: '', status: 'Not Started', type: 'Core', owner: currentUserSlug });
      setShowAddStep(false);
      await loadDevPlan();
    } catch (err) {
      notify.error(err.response?.data?.message || 'Failed to add step');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteStep = () => {
    if (!selectedStep) return;
    notify.confirm(`Delete Step ${selectedStep.step}: ${selectedStep.name}?`, async () => {
      try {
        setSaving(true);
        await devFilesApi.deleteDevPlanStep(selectedStep.step);
        setIsEditing(false);
        setSelectedStep(null);
        await loadDevPlan();
      } catch (err) {
        notify.error(err.response?.data?.message || 'Failed to delete step');
      } finally {
        setSaving(false);
      }
    });
  };

  const openCloseStepModal = () => {
    if (!selectedStep || !devPlan?.steps) return;
    setCloseStepPage(1);
    // Category starts blank on purpose: the rev log entry's kind of change is a choice, not a default.
    setCloseStepForm({ description: selectedStep.description || '', comment: selectedStep.comment || '', category: '' });
    // Compute renumber preview immediately
    const completingStep = devPlan.steps.find(s => s.step === selectedStep.step);
    if (!completingStep) return;
    const completed = devPlan.steps.filter(s => s.status === 'Complete').sort((a, b) => a.step - b.step);
    const active = devPlan.steps.filter(s => s.status !== 'Complete' && s.step !== selectedStep.step).sort((a, b) => a.step - b.step);
    const result = [];
    let seq = 1;
    completed.forEach(s => result.push({ ...s, newStep: seq++ }));
    result.push({ ...completingStep, status: 'Complete', newStep: seq++ });
    active.forEach(s => result.push({ ...s, newStep: seq++ }));
    setCompletePreviewItems(result);
    setShowCloseStep(true);
  };

  const handleApplyComplete = async () => {
    if (!selectedStep) return;
    try {
      setSaving(true);
      // Save any description/notes edits made in the complete step modal
      if (closeStepForm.description !== (selectedStep.description || '') || closeStepForm.comment !== (selectedStep.comment || '')) {
        await devFilesApi.updateDevPlanStep(selectedStep.step, { ...selectedStep, description: closeStepForm.description, comment: closeStepForm.comment });
      }
      const summary = `Step ${selectedStep.step}: ${selectedStep.name}`;
      const changes = closeStepForm.description?.split('\n').map(c => c.trim()).filter(c => c.length > 0) || [];
      await devFilesApi.closeDevPlanStep(selectedStep.step, summary, changes);
      const updates = completePreviewItems.map(item => ({ id: item.id, step: item.newStep, owner: item.owner }));
      await devFilesApi.reorderDevPlan(updates);
      // Bump major version on step completion. Shared with the Rev Log add dialog so the
      // two cannot drift — this copy used to drop zero padding (b01.000 would have majored
      // to b2.000 rather than b02.000 after the beta reset).
      const revRes = await devFilesApi.getRevLog();
      const currentVer = revRes.data.currentVersion || 'a0.000';
      const newVersion = suggestNextVersion(currentVer, 'major');
      await devFilesApi.addRevision({
        version: newVersion,
        type: 'major',
        category: closeStepForm.category,
        summary: `Step ${selectedStep.step} Complete: ${selectedStep.name}`,
        changes: changes.length > 0 ? changes : [`Completed: ${selectedStep.name}`]
      });
      setShowCloseStep(false);
      setCloseStepForm({ description: '', comment: '', category: '' });
      window.dispatchEvent(new Event('pmd-version-updated'));
      await loadDevPlan();
    } catch (err) {
      notify.error(typeof err.response?.data === 'string' ? err.response.data : err.response?.data?.message || 'Failed to complete step');
    } finally {
      setSaving(false);
    }
  };

  // ── Datasheet handlers ───────────────────────────────────────────────────────
  const handleDsEdit = (id, field, value) => {
    setDsEdits(prev => ({
      ...prev,
      [id]: { ...(prev[id] || {}), [field]: value }
    }));
  };

  const handlePreviewStepEdits = () => {
    setDsEdits(prev => {
      const allItems = devPlan.steps.map(s => ({
        id: s.id,
        step: s.step,
        newStep: prev[s.id]?.step ?? s.step,
        edited: prev[s.id]?.step !== undefined
      }));
      const sorted = [...allItems].sort((a, b) => {
        if (a.newStep !== b.newStep) return a.newStep - b.newStep;
        if (a.edited && !b.edited) return -1;
        if (!a.edited && b.edited) return 1;
        return a.step - b.step;
      });
      for (let i = 1; i < sorted.length; i++) {
        if (sorted[i].newStep <= sorted[i - 1].newStep) {
          sorted[i] = { ...sorted[i], newStep: sorted[i - 1].newStep + 1 };
        }
      }
      const next = { ...prev };
      sorted.forEach(item => {
        if (item.newStep !== item.step) {
          next[item.id] = { ...(next[item.id] || {}), step: item.newStep };
        } else if (next[item.id]) {
          const { step, ...rest } = next[item.id]; // eslint-disable-line no-unused-vars
          if (Object.keys(rest).length === 0) delete next[item.id];
          else next[item.id] = rest;
        }
      });
      return next;
    });
    setDsSortPending(true);
  };

  const handleDsCancel = () => { setDsEdits({}); setDsEditMode(false); setDsSortPending(false); };

  const handleConsolidateCompleted = () => {
    notify.confirm('Move all completed steps to the front and renumber sequentially?', async () => {
      try {
        setSaving(true);
        const complete = [...devPlan.steps].filter(s => s.status === 'Complete').sort((a, b) => a.step - b.step);
        const active = [...devPlan.steps].filter(s => s.status !== 'Complete').sort((a, b) => a.step - b.step);
        const updates = [...complete, ...active].map((s, i) => ({ id: s.id, step: i + 1 }));
        await devFilesApi.reorderDevPlan(updates);
        await loadDevPlan();
      } catch (err) {
        notify.error('Failed to consolidate completed steps');
      } finally {
        setSaving(false);
      }
    });
  };

  const handleRemoveStepGaps = () => {
    notify.confirm('Renumber all steps sequentially to remove gaps?', async () => {
      try {
        setSaving(true);
        const sorted = [...devPlan.steps].sort((a, b) => a.step - b.step);
        const updates = sorted.map((s, i) => ({ id: s.id, step: i + 1, owner: s.owner }));
        await devFilesApi.reorderDevPlan(updates);
        await loadDevPlan();
      } catch (err) {
        notify.error('Failed to remove step gaps');
      } finally {
        setSaving(false);
      }
    });
  };

  const handleDsSave = async () => {
    const dirtyIds = Object.keys(dsEdits);
    if (dirtyIds.length === 0) return;
    try {
      setSaving(true);
      const hasStepChanges = dirtyIds.some(id => dsEdits[id].step !== undefined);

      // Non-step field changes — use original step number as key, send full merged object
      const fieldDirty = dirtyIds.filter(id => {
        const e = dsEdits[id];
        return e.name !== undefined || e.type !== undefined || e.status !== undefined ||
               e.owner !== undefined || e.description !== undefined || e.comment !== undefined;
      });
      for (const id of fieldDirty) {
        const original = devPlan.steps.find(s => s.id === parseInt(id));
        if (!original) continue;
        const merged = { ...original, ...dsEdits[id] };
        await devFilesApi.updateDevPlanStep(original.step, merged);
      }

      // Step reorder — owner is omitted here; field update loop above already handled owner changes
      if (hasStepChanges) {
        const updates = devPlan.steps.map(s => ({
          id: s.id,
          step: dsEdits[s.id]?.step ?? s.step
        }));
        await devFilesApi.reorderDevPlan(updates);
      }

      setDsEdits({});
      setDsEditMode(false);
      setDsSortPending(false);
      notify.success('Changes saved');
      await loadDevPlan();
    } catch (err) {
      notify.error(err.response?.data?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const openDescModal = (step, editMode) => {
    const merged = { ...step, ...(dsEdits[step.id] || {}) };
    setDescModalStep(step);
    setDescLocal({ description: merged.description || '', comment: merged.comment || '' });
    setDescModalEditMode(editMode);
    setShowDescModal(true);
  };

  const handleDescApply = () => {
    if (!descModalStep) return;
    setDsEdits(prev => ({
      ...prev,
      [descModalStep.id]: { ...(prev[descModalStep.id] || {}), ...descLocal }
    }));
    setShowDescModal(false);
  };

  // ── Computed ─────────────────────────────────────────────────────────────────
  const dsHasStepChanges = Object.values(dsEdits).some(e => e.step !== undefined);
  // The one trunk both views read (sort -> search -> filters), so list view and the datasheet
  // cannot disagree about which steps exist or in what order. See hooks/useListTrunk.
  const { searched: searchedSteps, filtered: filteredSteps } = useListTrunk({
    rows: devPlan?.steps, query: searchTerm, searchFields: SEARCH_FIELDS, labels: SEARCH_FIELD_LABELS,
    filters: dsFilters, sortConfig: dsSort,
  });

  // Datasheet-only ORDERING, not membership: while step numbers are being renumbered in place,
  // this previews the pending order. It reorders the shared trunk without changing which rows are
  // in it, so the two views still agree on the row set.
  const dsDisplayData = useMemo(() => (
    dsSortPending
      ? [...filteredSteps].sort((a, b) => (dsEdits[a.id]?.step ?? a.step) - (dsEdits[b.id]?.step ?? b.step))
      : filteredSteps
  ), [filteredSteps, dsSortPending, dsEdits]);
  const dsIsDirty = Object.keys(dsEdits).length > 0;
  const dsHasFilters = hasColumnFilters(dsFilters);

  // What the detail pane shows. Never a row the list is not showing — see resolveSelection. It
  // matches on id, so a reload after renumbering still finds the same step.
  const selectedStep = useMemo(
    () => resolveSelection(filteredSteps, pickedStep, { keep: isEditing }),
    [filteredSteps, pickedStep, isEditing]
  );

  // A new history entry under a mounted page — clicking this page's own nav item — starts clean
  // (or restores, if the entry was visited before). A remount already reads it via useState.
  const entryKeyRef = useRef(memoryKey);
  useEffect(() => {
    if (entryKeyRef.current === memoryKey) return;
    entryKeyRef.current = memoryKey;
    const s = loadView(memoryKey);
    setViewMode(s?.view === 'datasheet' ? 'datasheet' : 'list');
    setSearchTerm(s?.search || '');
    setDsFilters(dropHandleFilters(s?.filters) || { status: ['Not Started', 'Pending', 'In Progress'] });
    setDsSort(s?.sort?.key ? s.sort : null);
    setSelectedStep(s?.picked != null ? { id: s.picked } : null);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [memoryKey]);

  // Remember this page's state against the current history entry.
  useEffect(() => {
    saveView(memoryKey, { view: viewMode, search: searchTerm, filters: dsFilters, sort: dsSort, picked: (selectedStep ?? pickedStep)?.id ?? null });
  }, [memoryKey, viewMode, searchTerm, dsFilters, dsSort, selectedStep, pickedStep]);

  // Measures the shared trunk, so the chip agrees with whichever view is on screen.
  const dsFilteredCount = filteredSteps.length;

  // Columns for FilterableDataGrid
  useEffect(() => {
    devFilesApi.getDevTrackRoster()
      .then(res => setRoster(res.data?.members || []))
      .catch(() => setRoster([]));
  }, []);

  const dsColumns = useMemo(() => [
    { key: 'step',   fieldName: 'step',   label: '#',      width: 60  },
    { key: 'name',   fieldName: 'name',   label: 'Name',   width: 520 },
    { key: 'type',   fieldName: 'type',   label: 'Type',   width: 110, formatter: (v) => v ? <span className={`${getTypeClass(v)}`}>{v}</span> : null },
    { key: 'status', fieldName: 'status', label: 'Status', width: 120, formatter: (v) => v ? <span className={`${getStatusClass(v)}`}>{v}</span> : null },
    // key is the resolved name (what the filter lists and matches), fieldName the handle (what edits write).
    { key: 'ownerName', fieldName: 'owner', label: 'Owner', width: 100, renderCell: (row) => row.ownerName || formatOwner(row.owner) },
  ], [roster, typeOptions]); // eslint-disable-line react-hooks/exhaustive-deps

  // Editable cell renderer for datasheet edit mode
  const renderDsCell = (row, col, displayValue, isEdited) => {
    switch (col.fieldName) {
      case 'step':
        return (
          <input
            type="number"
            className="stui-ds-cell-input stui-cell-center"
            value={displayValue ?? ''}
            onChange={e => handleDsEdit(row.id, 'step', parseInt(e.target.value) || row.step)}
            min={1}
            style={{ width: '48px' }}
          />
        );
      case 'name':
        return <input className="stui-ds-cell-input stui-w-full" value={displayValue ?? ''} onChange={e => handleDsEdit(row.id, 'name', e.target.value)} />;
      case 'type':
        return (
          <StuiSelect className="stui-ds-cell-input" value={displayValue ?? ''} onChange={e => handleDsEdit(row.id, 'type', e.target.value)}>
            {typeOptions.map(t => <option key={t} value={t}>{t}</option>)}
          </StuiSelect>
        );
      case 'status':
        if (row.status === 'Complete') return <span className="step-status-badge status-complete">Complete</span>;
        return (
          <StuiSelect className="stui-ds-cell-input" value={displayValue ?? ''} onChange={e => handleDsEdit(row.id, 'status', e.target.value)}>
            {statusOptions.map(v => <option key={v} value={v}>{v}</option>)}
          </StuiSelect>
        );
      case 'owner':
        return (
          <StuiSelect className="stui-ds-cell-input" value={(isEdited ? displayValue : row.owner) ?? ''} onChange={e => handleDsEdit(row.id, 'owner', e.target.value || null)}>
            <option value="">Unassigned</option>
            {roster.map(m => <option key={m.slug} value={m.slug}>{m.displayName}</option>)}
          </StuiSelect>
        );
      default:
        return displayValue;
    }
  };

  const getProgress = () => {
    if (!devPlan?.steps) return { complete: 0, total: 0, percent: 0 };
    const complete = devPlan.steps.filter(s => s.status === 'Complete').length;
    const total = devPlan.steps.length;
    return { complete, total, percent: total ? Math.round((complete / total) * 100) : 0 };
  };



  const getStatusClass = (status) => {
    switch (status) {
      case 'Complete':    return 'stui-badge-green';
      case 'In Progress': return 'stui-badge-orange';
      case 'Pending':     return 'stui-badge-amber';
      case 'Validate':    return 'stui-badge-blue';
      default:            return 'stui-badge-gray';
    }
  };

  const getTypeClass = (type) => {
    switch (type) {
      case 'Core':           return 'stui-badge-indigo';
      case 'Feature':        return 'stui-badge-purple';
      case 'Milestone':      return 'stui-badge-amber';
      case 'Infrastructure': return 'stui-badge-green';
      case 'UI':             return 'stui-badge-blue';
      case 'Refactor':       return 'stui-badge-teal';
      case 'DevOps':         return 'stui-badge-orange';
      case 'Security':       return 'stui-badge-red';
      case 'Maintenance':    return 'stui-badge-gray';
      default:               return 'stui-badge-gray';
    }
  };

  const formatOwner = (slug) => {
    if (!slug) return 'Unassigned';
    const member = roster.find(m => m.slug?.toLowerCase() === slug.toLowerCase());
    return member ? member.displayName : slug;
  };


  const fieldDirty = (field) => isEditing && editForm && selectedStep && editForm[field] !== selectedStep[field];

  const hasChanges = isEditing && editForm && selectedStep && (
    editForm.name !== selectedStep.name ||
    editForm.description !== selectedStep.description ||
    editForm.comment !== selectedStep.comment ||
    editForm.status !== selectedStep.status ||
    editForm.type !== selectedStep.type ||
    editForm.owner !== selectedStep.owner
  );

  if (loading) return <div className="stui-module-loading">Loading...</div>;

  const progress = getProgress();
  // Reads the shared trunk. Completed steps used to be excluded here unconditionally, which made
  // them permanently unfindable in list view even by an exact-name search; the dsFilters seed
  // already hides them by default and is clearable from the header, so the hardcode is gone.
  const listSteps = filteredSteps;

  return (
    <div className="stui-module-container">
      {/* ── Header ── */}
      <div className="stui-module-header">
        <div className="stui-module-header-left">
          <ClipboardList size={20} />
          <h1>Development Plan</h1>
          <div className="stui-module-header-tabs">
            <button onClick={() => setViewMode('list')} className={`stui-tab-btn-icon ${viewMode === 'list' ? 'active' : ''}`} title="List View"><List size={18} /></button>
            <button onClick={() => setViewMode('datasheet')} className={`stui-tab-btn-icon ${viewMode === 'datasheet' ? 'active' : ''}`} title="Datasheet View"><Table size={18} /></button>
          </div>
          <span className="stui-module-header-meta">
            {progress.total} steps &bull; {progress.complete} complete ({progress.percent}%)
          </span>
        </div>
        <div className="stui-module-header-actions">
          {/* List view CRUD */}
          {viewMode === 'list' && (
            <>
              {isEditing
                ? <button className="stui-crud-btn stui-delete-btn" onClick={handleDeleteStep} disabled={saving} title="Delete"><Trash2 size={18} /></button>
                : <button className="stui-crud-btn" onClick={() => { setNewStep(prev => ({ ...prev, owner: currentUserSlug })); setShowAddStep(true); }} title="Add Step"><Plus size={18} /></button>
              }
              <button className={`stui-crud-btn ${!selectedStep && !isEditing ? 'disabled' : ''}`} onClick={isEditing ? cancelEditing : startEditing} disabled={!selectedStep && !isEditing} title={isEditing ? 'Cancel' : 'Edit'}>
                {isEditing ? <X size={18} /> : <Pencil size={18} />}
              </button>
              <button className={`stui-crud-btn ${!isEditing || !hasChanges ? 'disabled' : ''}`} onClick={handleSave} disabled={!isEditing || saving || !hasChanges} title="Save"><Save size={18} /></button>
            </>
          )}

          {/* Datasheet controls */}
          {viewMode === 'datasheet' && (
            <>
              <button className="stui-crud-btn" onClick={dsEditMode ? handleDsCancel : () => setDsEditMode(true)} title={dsEditMode ? 'Cancel' : 'Edit'}>
                {dsEditMode ? <X size={18} /> : <Pencil size={18} />}
              </button>
              <button className={`stui-crud-btn ${!dsIsDirty || saving ? 'disabled' : ''}`} onClick={handleDsSave} disabled={!dsIsDirty || saving} title="Save"><Save size={18} /></button>
            </>
          )}

          {/* Clear filter — always visible when filters active */}
          {dsHasFilters && (
            <button onClick={() => setDsFilters({})} className="stui-clear-filter-btn" title="Clear filters">
              <FilterX size={16} /><span>{dsFilteredCount}/{devPlan?.steps?.length || 0}</span>
            </button>
          )}

          {/* Search */}
          <div className="stui-search-wrapper">
            <SearchClearIcon searchTerm={searchTerm} onClear={() => setSearchTerm('')} />
            <input type="text" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} placeholder="Search steps..." title={SEARCH_HINT} className="stui-search-input" />
          </div>


          {/* Action menu — list: Refresh + Complete Step; datasheet: Refresh */}
          <div className="stui-action-menu" ref={viewMode === 'list' ? menuRef : dsMenuRef}>
            <button className="stui-action-menu-btn" onClick={() => viewMode === 'list' ? setMenuOpen(!menuOpen) : setDsMenuOpen(!dsMenuOpen)} title="Actions"><GripVertical size={16} /></button>
            {viewMode === 'list' && menuOpen && (
              <div className="stui-action-menu-dropdown">
                <button className="stui-action-menu-item" onClick={() => { setMenuOpen(false); setShowAdvSearch(true); }}><ListFilter size={16} /><span>Advanced Search…</span></button>
                <button className="stui-action-menu-item" onClick={() => { setMenuOpen(false); loadDevPlan(); }}>
                  <RefreshCw size={16} /><span>Refresh</span>
                </button>
                <div className="stui-action-menu-divider" />
                {selectedStep && selectedStep.status !== 'Complete' && (
                  <button className="stui-action-menu-item" onClick={() => { setMenuOpen(false); openCloseStepModal(); }}>
                    <Check size={16} /><span>Complete Step…</span>
                  </button>
                )}
                <button className="stui-action-menu-item" onClick={() => { setMenuOpen(false); handleConsolidateCompleted(); }}>
                  <ListChecks size={16} /><span>Consolidate Completed</span>
                </button>
              </div>
            )}
            {viewMode === 'datasheet' && dsMenuOpen && (
              <div className="stui-action-menu-dropdown">
                <button className="stui-action-menu-item" onClick={() => { setDsMenuOpen(false); setShowAdvSearch(true); }}><ListFilter size={16} /><span>Advanced Search…</span></button>
                <button className="stui-action-menu-item" onClick={() => { setDsMenuOpen(false); loadDevPlan(); }}>
                  <RefreshCw size={16} /><span>Refresh</span>
                </button>
                <div className="stui-action-menu-divider" />
                <button className="stui-action-menu-item" onClick={() => { setDsMenuOpen(false); handleRemoveStepGaps(); }}>
                  <ListOrdered size={16} /><span>Remove Step Gaps</span>
                </button>
                {dsHasStepChanges && (
                  <button className="stui-action-menu-item" onClick={() => { setDsMenuOpen(false); handlePreviewStepEdits(); }}>
                    <ArrowUpDown size={16} /><span>Preview Step Edits…</span>
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Progress bar */}
      <div className="stui-progress">
        <div className="stui-progress-bar" style={{ width: `${progress.percent}%` }} />
      </div>

      {/* ── List View ── */}
      {viewMode === 'list' && (
        <div className={`stui-list-view ${listCollapsed ? 'stui-collapsed' : ''}`}>
          {listCollapsed ? (
            <div className="stui-list-panel stui-collapsed">
              <button className="stui-list-panel-expand-btn" onClick={() => setListCollapsed(false)} title="Expand list">
                <ChevronRight size={16} />
              </button>
            </div>
          ) : (
            <div className="stui-list-panel">
              <div className="stui-list-panel-header">
                <button className="stui-list-panel-collapse-btn" onClick={() => setListCollapsed(true)} title="Collapse list">
                  <ChevronLeft size={16} />
                </button>
              </div>
              <div className="stui-list-scroll">
                {listSteps.map(step => (
                  <button
                    key={step.id}
                    onClick={() => handleSelectStep(step)}
                    className={`stui-list-item ${selectedStep?.step === step.step ? 'selected' : ''}`}
                  >
                    <span className="stui-list-item-primary">
                      <span className="stui-list-item-step-num">{step.step}</span>
                      {step.name}
                    </span>
                    <span className="stui-list-item-secondary">
                      {formatOwner(step.owner)} &bull; {step.status}
                    </span>
                  </button>
                ))}
                {listSteps.length === 0 && <div className="stui-list-empty">{devPlan?.steps?.length ? NO_MATCH_LIST : 'No steps'}</div>}
              </div>
            </div>
          )}

          <div className="stui-detail-panel">
            {selectedStep ? (
              <div className="stui-detail-content">
                <div className="stui-detail-grid">

                  {/* General Information */}
                  <div className="stui-col-24">
                    <div className="stui-detail-section-header">General Information</div>
                  </div>

                  <div className="stui-field-group stui-col-4">
                    <label>Step</label>
                    <input type="text" className="stui-field-input" value={selectedStep.step} readOnly onChange={() => {}} />
                  </div>
                  <div className="stui-field-group stui-col-20">
                    <label>Name</label>
                    {isEditing
                      ? <input type="text" className={`stui-field-input${fieldDirty('name') ? ' dirty' : ''}`} value={editForm.name} onChange={e => setEditForm({ ...editForm, name: e.target.value })} autoFocus />
                      : <input type="text" className="stui-field-input" value={selectedStep.name} readOnly onChange={() => {}} />
                    }
                  </div>

                  <div className="stui-field-group stui-col-8">
                    <label>Type</label>
                    {isEditing
                      ? <StuiSelect className={`stui-field-input${fieldDirty('type') ? ' dirty' : ''}`} value={editForm.type || 'Core'} onChange={e => setEditForm({ ...editForm, type: e.target.value })}>
                          {typeOptions.map(t => <option key={t} value={t}>{t}</option>)}
                        </StuiSelect>
                      : <span className={`${getTypeClass(selectedStep.type)}`}>{selectedStep.type || 'Core'}</span>
                    }
                  </div>
                  <div className="stui-field-group stui-col-8">
                    <label>Status</label>
                    {isEditing
                      ? <StuiSelect className={`stui-field-input${fieldDirty('status') ? ' dirty' : ''}`} value={editForm.status} onChange={e => setEditForm({ ...editForm, status: e.target.value })}>
                          {statusOptions.map(v => <option key={v} value={v}>{v}</option>)}
                        </StuiSelect>
                      : <span className={`${getStatusClass(selectedStep.status)}`}>{selectedStep.status}</span>
                    }
                  </div>
                  <div className="stui-field-group stui-col-8">
                    <label>Owner</label>
                    {isEditing
                      ? <StuiSelect className={`stui-field-input${fieldDirty('owner') ? ' dirty' : ''}`} value={editForm.owner || ''} onChange={e => setEditForm({ ...editForm, owner: e.target.value || null })}>
                          <option value="">Unassigned</option>
                          {roster.map(m => <option key={m.slug} value={m.slug}>{m.displayName}</option>)}
                        </StuiSelect>
                      : <span className="stui-badge-owner">{formatOwner(selectedStep.owner)}</span>
                    }
                  </div>

                  {/* Step Details */}
                  <div className="stui-col-24">
                    <div className="stui-detail-section-header">Step Details</div>
                  </div>

                  <div className="stui-field-group stui-col-24">
                    <label>Description</label>
                    {isEditing
                      ? <textarea className={`stui-field-input resizable${fieldDirty('description') ? ' dirty' : ''}`} value={editForm.description || ''} onChange={e => setEditForm({ ...editForm, description: e.target.value })} rows={5} />
                      : <textarea className="stui-field-input resizable" value={selectedStep.description || ''} readOnly rows={5} />
                    }
                  </div>

                  <div className="stui-field-group stui-col-24">
                    <label>Notes</label>
                    {isEditing
                      ? <textarea className={`stui-field-input resizable${fieldDirty('comment') ? ' dirty' : ''}`} value={editForm.comment || ''} onChange={e => setEditForm({ ...editForm, comment: e.target.value })} rows={8} placeholder="Additional details, sub-tasks, or implementation notes..." />
                      : <textarea className="stui-field-input resizable" value={selectedStep.comment || ''} readOnly rows={8} />
                    }
                  </div>

                </div>
              </div>
            ) : (
              <div className="stui-detail-empty">
                {(devPlan?.steps?.length || 0) > 0 && filteredSteps.length === 0 ? (
                  <NoMatchState hasSearch={!!searchTerm} hasFilters={dsHasFilters} onClearSearch={() => setSearchTerm('')} onClearFilters={() => setDsFilters({})} />
                ) : <p>Select a step to view details</p>}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Datasheet View ── */}
      {viewMode === 'datasheet' && (
        <div className="stui-datasheet-view">
          <FilterableDataGrid
            data={dsDisplayData}
            allData={searchedSteps}
            sortConfig={dsSort || { key: null, direction: null }}
            onSortChange={setDsSort}
            columns={dsColumns}
            filters={dsFilters}
            setFilters={setDsFilters}
            editModeEnabled={dsEditMode}
            editedData={dsEdits}
            renderEditableCell={renderDsCell}
            onRowDoubleClick={(row) => openDescModal(row, dsEditMode)}
          />
        </div>
      )}

      {/* ── Add Step Modal ── */}
      {showAddStep && (
        <div className="stui-modal-overlay">
          <div className="stui-modal stui-modal-md">
            <div className="stui-modal-header">
              <h3>New Step</h3>
              <button className="stui-modal-close" onClick={() => setShowAddStep(false)}><X size={20} /></button>
            </div>
            <div className="stui-modal-body">
              <div className="stui-detail-grid">
                <div className="stui-col-24">
                  <div className="stui-detail-section-header">General Information</div>
                </div>
                <div className="stui-field-group stui-col-24">
                  <label>Name</label>
                  <input type="text" className="stui-field-input" value={newStep.name} onChange={e => setNewStep({ ...newStep, name: e.target.value })} placeholder="Step name" autoFocus />
                </div>
                <div className="stui-field-group stui-col-8">
                  <label>Type</label>
                  <StuiSelect className="stui-field-input" value={newStep.type} onChange={e => setNewStep({ ...newStep, type: e.target.value })}>
                    {typeOptions.map(t => <option key={t} value={t}>{t}</option>)}
                  </StuiSelect>
                </div>
                <div className="stui-field-group stui-col-8">
                  <label>Status</label>
                  <StuiSelect className="stui-field-input" value={newStep.status} onChange={e => setNewStep({ ...newStep, status: e.target.value })}>
                    <option value="Not Started">Not Started</option>
                    <option value="Pending">Pending</option>
                    <option value="In Progress">In Progress</option>
                  </StuiSelect>
                </div>
                <div className="stui-field-group stui-col-8">
                  <label>Owner</label>
                  <StuiSelect className="stui-field-input" value={newStep.owner || ''} onChange={e => setNewStep({ ...newStep, owner: e.target.value || null })}>
                    <option value="">Unassigned</option>
                    {roster.map(m => <option key={m.slug} value={m.slug}>{m.displayName}</option>)}
                  </StuiSelect>
                </div>
                <div className="stui-col-24">
                  <div className="stui-detail-section-header">Step Details</div>
                </div>
                <div className="stui-field-group stui-col-24">
                  <label>Description</label>
                  <textarea className="stui-field-input resizable" value={newStep.description || ''} onChange={e => setNewStep({ ...newStep, description: e.target.value })} placeholder="What does this step involve?" rows={5} />
                </div>
                <div className="stui-field-group stui-col-24">
                  <label>Notes</label>
                  <textarea className="stui-field-input resizable" value={newStep.comment || ''} onChange={e => setNewStep({ ...newStep, comment: e.target.value })} placeholder="Additional details or sub-tasks" rows={5} />
                </div>
              </div>
            </div>
            <div className="stui-modal-footer">
              <button className="stui-btn stui-btn-secondary" onClick={() => setShowAddStep(false)}>Cancel</button>
              <button className="stui-btn stui-btn-primary" onClick={handleAddStep} disabled={saving || !newStep.name.trim()}>{saving ? 'Adding...' : 'Add Step'}</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Complete Step Modal (2-page wizard) ── */}
      {showCloseStep && selectedStep && (
        <div className="stui-modal-overlay">
          <div className="stui-modal stui-modal-lg">
            <div className="stui-modal-header">
              <h3>{closeStepPage === 1 ? 'Complete Step — Details' : 'Complete Step — Renumbering'}</h3>
              <button className="stui-modal-close" onClick={() => { setShowCloseStep(false); setCloseStepPage(1); }}><X size={20} /></button>
            </div>

            {closeStepPage === 1 ? (
              <>
                <div className="stui-modal-body">
                  <div className="stui-detail-grid">
                    {/* General Information */}
                    <div className="stui-col-24">
                      <div className="stui-detail-section-header">General Information</div>
                    </div>
                    <div className="stui-field-group stui-col-4">
                      <label>Step</label>
                      <input type="text" className="stui-field-input" value={selectedStep.step} readOnly onChange={() => {}} />
                    </div>
                    <div className="stui-field-group stui-col-20">
                      <label>Name</label>
                      <input type="text" className="stui-field-input" value={selectedStep.name} readOnly onChange={() => {}} />
                    </div>
                    <div className="stui-field-group stui-col-8">
                      <label>Type</label>
                      <span className={`${getTypeClass(selectedStep.type)}`}>{selectedStep.type || 'Core'}</span>
                    </div>
                    <div className="stui-field-group stui-col-8">
                      <label>Status</label>
                      <span className={`${getStatusClass(selectedStep.status)}`}>{selectedStep.status}</span>
                    </div>
                    <div className="stui-field-group stui-col-8">
                      <label>Rev Log Category <span className="stui-required">*</span></label>
                      <StuiSelect className="stui-field-input" value={closeStepForm.category} onChange={e => setCloseStepForm({ ...closeStepForm, category: e.target.value })}>
                        <option value="">-- Select --</option>
                        {revLogCategoryOptions.map(c => <option key={c} value={c}>{c}</option>)}
                      </StuiSelect>
                    </div>
                    {/* Step Details */}
                    <div className="stui-col-24">
                      <div className="stui-detail-section-header">Step Details</div>
                    </div>
                    <div className="stui-field-group stui-col-24">
                      <label>Description</label>
                      <textarea className="stui-field-input resizable" value={closeStepForm.description} onChange={e => setCloseStepForm({ ...closeStepForm, description: e.target.value })} rows={4} />
                    </div>
                    <div className="stui-field-group stui-col-24">
                      <label>Notes</label>
                      <textarea className="stui-field-input resizable" value={closeStepForm.comment} onChange={e => setCloseStepForm({ ...closeStepForm, comment: e.target.value })} rows={4} />
                    </div>
                  </div>
                </div>
                <div className="stui-modal-footer">
                  <button className="stui-btn stui-btn-primary" onClick={() => setCloseStepPage(2)} disabled={!closeStepForm.category} title={closeStepForm.category ? undefined : 'Pick the rev log Category first'}>Next</button>
                </div>
              </>
            ) : (
              <>
                <div className="stui-modal-body stui-modal-body-grid">
                  <p className="stui-modal-hint">
                    Step {selectedStep.step} moves to position {completePreviewItems.find(i => i.id === selectedStep.id)?.newStep || '?'} — all steps renumber sequentially. {completePreviewItems.filter(i => i.step !== i.newStep).length} step(s) will renumber.
                  </p>
                  <div className="stui-modal-scroll">
                    <table className="stui-data-grid stui-td-xs">
                      <thead>
                        <tr>
                          <th>Step</th>
                          <th>New</th>
                          <th>Name</th>
                          <th>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {completePreviewItems.map(item => {
                          const changed = item.step !== item.newStep;
                          const isCompleting = item.id === selectedStep.id;
                          return (
                            <tr key={item.id} className={isCompleting ? 'stui-row-completing' : changed ? 'stui-row-changed' : ''}>
                              <td className="stui-td-center stui-td-muted">{item.step}</td>
                              <td className={`stui-td-center${changed ? ' stui-td-accent' : ''}`}>{item.newStep}</td>
                              <td>{item.name}</td>
                              <td className={isCompleting ? 'stui-td-success' : 'stui-td-dim'}>{isCompleting ? 'Complete' : item.status}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
                <div className="stui-modal-footer">
                  <button className="stui-btn stui-btn-secondary" onClick={() => setCloseStepPage(1)}>Back</button>
                  <button className="stui-btn stui-btn-primary" onClick={handleApplyComplete} disabled={saving}>{saving ? 'Completing...' : 'Complete & Renumber'}</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}


      {/* ── Description Modal ── */}
      {showDescModal && descModalStep && (
        <div className="stui-modal-overlay">
          <div className="stui-modal stui-modal-lg">
            <div className="stui-modal-header">
              <h3>Step Details — {descModalStep.step} — {descModalStep.name}</h3>
              <button className="stui-modal-close" onClick={() => setShowDescModal(false)}><X size={20} /></button>
            </div>
            <div className="stui-modal-body">
              <div className="stui-field-group">
                <label>Description</label>
                {descModalEditMode ? (
                  <textarea className="stui-field-input resizable" value={descLocal.description} onChange={e => setDescLocal(p => ({ ...p, description: e.target.value }))} rows={10} autoFocus />
                ) : (
                  <textarea className="stui-field-input resizable" value={descLocal.description || ''} readOnly rows={10} onChange={() => {}} />
                )}
              </div>
              <div className="stui-field-group">
                <label>Notes</label>
                {descModalEditMode ? (
                  <textarea className="stui-field-input resizable" value={descLocal.comment} onChange={e => setDescLocal(p => ({ ...p, comment: e.target.value }))} rows={10} placeholder="Additional details, sub-tasks, or implementation notes..." />
                ) : (
                  <textarea className="stui-field-input resizable" value={descLocal.comment || ''} readOnly rows={10} onChange={() => {}} />
                )}
              </div>
            </div>
            {descModalEditMode && (
              <div className="stui-modal-footer">
                <button className="stui-btn stui-btn-secondary" onClick={() => setShowDescModal(false)}>Cancel</button>
                <button className="stui-btn stui-btn-primary" onClick={handleDescApply}>Apply</button>
              </div>
            )}
          </div>
        </div>
      )}
      <AdvancedSearchModal isOpen={showAdvSearch} onClose={() => setShowAdvSearch(false)} query={searchTerm} fields={SEARCH_FIELD_LABELS} onApply={setSearchTerm} />
    </div>
  );
};

export default DevPlanPage;
